import { useState, useEffect, useCallback, useRef } from "react";
import "./BreakoutGame.css";

// Define 6 unique progressively difficult themed levels
const LEVEL_CONFIGS = [
    {
        name: "Neon Gateway",
        difficulty: "Novice",
        diffColor: "#10b981", // Emerald Green
        description: "An open portal of low-density energy bricks. Learn the physics.",
        speed: 5,
        theme: {
            colors: ['#f87171', '#fb923c', '#fbbf24', '#38bdf8'],
            glow: '#f87171'
        },
        layout: [
            ["0","0","1","1","1","1","1","1","1","1","0","0"],
            ["0","1","1","1","1","1","1","1","1","1","1","0"],
            ["1","1","0","0","0","0","0","0","0","0","1","1"],
            ["1","0","0","0","0","0","0","0","0","0","0","1"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"]
        ]
    },
    {
        name: "Invader Construct",
        difficulty: "Easy",
        diffColor: "#10b981", // Emerald Green
        description: "An ancient cybernetic shape. Armored purple nodes form its eyes.",
        speed: 5.5,
        theme: {
            colors: ['#4ade80', '#2dd4bf', '#60a5fa', '#c084fc'],
            glow: '#4ade80'
        },
        layout: [
            ["0","0","1","0","0","0","0","0","0","1","0","0"],
            ["0","0","0","1","0","2","2","0","1","0","0","0"],
            ["0","0","1","1","2","2","2","2","1","1","0","0"],
            ["0","1","1","2","1","2","2","1","2","1","1","0"],
            ["1","1","1","1","1","1","1","1","1","1","1","1"],
            ["1","0","1","0","0","0","0","0","0","1","0","1"]
        ]
    },
    {
        name: "Shield Generator",
        difficulty: "Medium",
        diffColor: "#3b82f6", // Royal Blue
        description: "Solid obsidian pillars shield the generator. Slide behind them!",
        speed: 6.0,
        theme: {
            colors: ['#f472b6', '#38bdf8', '#4ade80'],
            glow: '#3b82f6'
        },
        layout: [
            ["2","2","2","2","2","2","2","2","2","2","2","2"],
            ["1","1","1","1","1","1","1","1","1","1","1","1"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"],
            ["0","U","U","0","0","U","U","0","0","U","U","0"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"]
        ]
    },
    {
        name: "Cyber Helix",
        difficulty: "Hard",
        diffColor: "#f59e0b", // Amber Orange
        description: "Twin twisting ribbons of heavy gold and purple energy bricks.",
        speed: 6.5,
        theme: {
            colors: ['#fbbf24', '#a78bfa', '#f472b6'],
            glow: '#fbbf24'
        },
        layout: [
            ["3","3","0","0","2","2","2","2","0","0","3","3"],
            ["0","3","3","0","2","0","0","2","0","3","3","0"],
            ["0","0","3","2","2","0","0","2","2","3","0","0"],
            ["0","0","2","2","1","1","1","1","2","2","0","0"],
            ["0","2","2","0","1","1","1","1","0","2","2","0"],
            ["2","2","0","0","0","0","0","0","0","0","2","2"]
        ]
    },
    {
        name: "Tungsten Maze",
        difficulty: "Expert",
        diffColor: "#ef4444", // Crimson Red
        description: "Tight vertical gravity shafts. Bouncing inside guarantees mayhem.",
        speed: 7.0,
        theme: {
            colors: ['#f43f5e', '#a855f7', '#ec4899'],
            glow: '#ec4899'
        },
        layout: [
            ["2","1","U","2","1","U","U","1","2","U","1","2"],
            ["2","1","U","2","1","U","U","1","2","U","1","2"],
            ["U","0","U","U","0","U","U","0","U","U","0","U"],
            ["1","0","0","2","0","0","0","0","2","0","0","1"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"],
            ["0","0","0","0","0","0","0","0","0","0","0","0"]
        ]
    },
    {
        name: "Hypernova Forge",
        difficulty: "Master",
        diffColor: "#ec4899", // Hot Pink
        description: "The supreme core matrix. A dense star protected by layers of shields.",
        speed: 7.5,
        theme: {
            colors: ['#f43f5e', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'],
            glow: '#f43f5e'
        },
        layout: [
            ["U","U","3","3","3","U","U","3","3","3","U","U"],
            ["U","3","2","2","2","3","3","3","2","2","2","U"],
            ["3","2","1","1","1","2","2","2","1","1","1","3"],
            ["3","2","1","U","1","2","2","2","1","U","1","3"],
            ["0","3","2","1","2","3","3","3","2","1","2","0"],
            ["0","0","3","2","3","0","0","0","3","2","3","0"]
        ]
    }
];

const BreakoutGame = ({ onBack, isPaused }) => {
    const canvasRef = useRef(null);
    const wrapperRef = useRef(null);
    const [score, setScore] = useState(0);
    const [round, setRound] = useState(1);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const maxRounds = LEVEL_CONFIGS.length;

    // Internal Game World Dimensions (for logic)
    const worldWidth = 1000;
    const worldHeight = 600;

    // Scaled constants (multipliers)
    const PADDLE_WIDTH_RATIO = 0.2;
    const PADDLE_HEIGHT_RATIO = 0.025;
    const BALL_RADIUS_RATIO = 0.015;
    const BRICK_ROWS = 6;
    const BRICK_COLS = 12; // Reduced for better mobile visibility
    const BRICK_PADDING_RATIO = 0.01;
    const BRICK_OFFSET_TOP_RATIO = 0.12;

    // Game state refs (normalized to worldWidth/worldHeight)
    const paddleX = useRef(worldWidth / 2 - (worldWidth * PADDLE_WIDTH_RATIO) / 2);
    const ball = useRef({
        x: worldWidth / 2,
        y: worldHeight - 100,
        dx: 5,
        dy: -5
    });
    const bricks = useRef([]);
    const animationFrameId = useRef(null);
    const activeKeys = useRef({ left: false, right: false });

    // Juicy effects refs
    const particles = useRef([]);
    const ballTrail = useRef([]);
    const screenShake = useRef(0);

    const initBricks = useCallback((roundIndex) => {
        const activeRound = roundIndex || 1;
        const config = LEVEL_CONFIGS[activeRound - 1] || LEVEL_CONFIGS[0];
        const layoutGrid = config.layout;
        const newBricks = [];

        for (let r = 0; r < BRICK_ROWS; r++) {
            newBricks[r] = [];
            for (let c = 0; c < BRICK_COLS; c++) {
                const type = layoutGrid[r]?.[c] || "0";
                let status = 0;
                let maxHits = 0;
                let isUnbreakable = false;
                let color = "#fff";

                if (type === "1") {
                    status = 1;
                    maxHits = 1;
                    color = config.theme.colors[c % config.theme.colors.length];
                } else if (type === "2") {
                    status = 2;
                    maxHits = 2;
                    color = "#c084fc"; // Armored magenta/purple
                } else if (type === "3") {
                    status = 3;
                    maxHits = 3;
                    color = "#fbbf24"; // Heavy gold
                } else if (type === "U") {
                    isUnbreakable = true;
                    color = "#06b6d4"; // Indestructible steel cyan
                }

                newBricks[r][c] = {
                    status,
                    maxHits,
                    isUnbreakable,
                    color
                };
            }
        }
        bricks.current = newBricks;
    }, [BRICK_ROWS, BRICK_COLS]);

    useEffect(() => {
        initBricks(round);
    }, [initBricks, round]);

    const spawnParticles = useCallback((x, y, color, count = 8) => {
        for (let i = 0; i < count; i++) {
            particles.current.push({
                x: x,
                y: y,
                dx: (Math.random() - 0.5) * 6,
                dy: (Math.random() - 0.75) * 6, // Biased upwards
                color: color,
                size: 2 + Math.random() * 3,
                life: 1.0,
                decay: 0.025 + Math.random() * 0.025,
                gravity: 0.12
            });
        }
    }, []);

    const draw = useCallback(() => {
        if (isPaused) {
            animationFrameId.current = requestAnimationFrame(draw);
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        // Match internal resolution to display size for sharpness
        const displayWidth = canvas.clientWidth;
        const displayHeight = canvas.clientHeight;
        if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
            canvas.width = displayWidth;
            canvas.height = displayHeight;
        }

        // Calculate scaling factors
        const scaleX = canvas.width / worldWidth;
        const scaleY = canvas.height / worldHeight;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // --- CAMERA SCREEN SHAKE ---
        ctx.save();
        if (screenShake.current > 0.1) {
            const shakeX = (Math.random() - 0.5) * screenShake.current;
            const shakeY = (Math.random() - 0.5) * screenShake.current;
            ctx.translate(shakeX, shakeY);
            screenShake.current *= 0.85; // Exponential decay
        }

        // Constants in screen pixels
        const sPaddleWidth = worldWidth * PADDLE_WIDTH_RATIO * scaleX;
        const sPaddleHeight = worldHeight * PADDLE_HEIGHT_RATIO * scaleY;
        const sBallRadius = worldWidth * BALL_RADIUS_RATIO * scaleX;
        const sBrickPadding = worldWidth * BRICK_PADDING_RATIO * scaleX;
        const sBrickOffsetTop = worldHeight * BRICK_OFFSET_TOP_RATIO * scaleY;
        const sBrickOffsetLeft = sBrickPadding * 2;
        const sBrickHeight = 25 * scaleY;

        const availableWidth = canvas.width - (sBrickOffsetLeft * 2);
        const sBrickWidth = (availableWidth - (BRICK_COLS - 1) * sBrickPadding) / BRICK_COLS;

        // Fetch current level configurations
        const levelConfig = LEVEL_CONFIGS[round - 1] || LEVEL_CONFIGS[0];

        // --- DRAW BRICKS & COLLISION ---
        let breakableBricksLeft = 0;
        const sBallX = ball.current.x * scaleX;
        const sBallY = ball.current.y * scaleY;
        const rPadding = sBallRadius * 0.8;

        bricks.current.forEach((row, r) => {
            row.forEach((brick, c) => {
                // Skip breakable bricks that are destroyed
                if (brick.status === 0 && !brick.isUnbreakable) return;

                const brickX = c * (sBrickWidth + sBrickPadding) + sBrickOffsetLeft;
                const brickY = r * (sBrickHeight + sBrickPadding) + sBrickOffsetTop;

                brick.screenX = brickX;
                brick.screenY = brickY;
                brick.screenWidth = sBrickWidth;
                brick.screenHeight = sBrickHeight;

                if (!brick.isUnbreakable) {
                    breakableBricksLeft++;
                }

                // --- ADVANCED EDGE-AWARE BRICK COLLISION ---
                if (sBallX + rPadding > brickX &&
                    sBallX - rPadding < brickX + sBrickWidth &&
                    sBallY + rPadding > brickY &&
                    sBallY - rPadding < brickY + sBrickHeight) {

                    // Determine which side of the brick was hit based on smallest penetration overlap
                    const overlapLeft = (sBallX + rPadding) - brickX;
                    const overlapRight = (brickX + sBrickWidth) - (sBallX - rPadding);
                    const overlapTop = (sBallY + rPadding) - brickY;
                    const overlapBottom = (brickY + sBrickHeight) - (sBallY - rPadding);

                    const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);

                    if (minOverlap === overlapLeft) {
                        ball.current.dx = -Math.abs(ball.current.dx); // Bounce Left
                    } else if (minOverlap === overlapRight) {
                        ball.current.dx = Math.abs(ball.current.dx); // Bounce Right
                    } else if (minOverlap === overlapTop) {
                        ball.current.dy = -Math.abs(ball.current.dy); // Bounce Up
                    } else if (minOverlap === overlapBottom) {
                        ball.current.dy = Math.abs(ball.current.dy); // Bounce Down
                    }

                    // Process hit damage
                    if (!brick.isUnbreakable) {
                        brick.status -= 1;
                        if (brick.status === 0) {
                            setScore(s => s + (brick.maxHits * 20)); // Bonus points for armored bricks
                            spawnParticles(brickX + sBrickWidth / 2, brickY + sBrickHeight / 2, brick.color, 12);
                            screenShake.current = 7;
                        } else {
                            setScore(s => s + 10);
                            spawnParticles(sBallX, sBallY, "#c084fc", 6);
                            screenShake.current = 3.5;
                        }
                    } else {
                        // Unbreakable obstacle hit: bright spark particles + score bounce
                        setScore(s => s + 5);
                        spawnParticles(sBallX, sBallY, "#06b6d4", 8);
                        screenShake.current = 5;
                    }
                }

                // --- DRAW BRICK STYLINGS ---
                ctx.save();
                if (brick.isUnbreakable) {
                    // Indestructible Gate styling
                    ctx.shadowBlur = 10;
                    ctx.shadowColor = "#06b6d4";
                    ctx.fillStyle = "#1e293b";
                    ctx.beginPath();
                    ctx.roundRect(brickX, brickY, sBrickWidth, sBrickHeight, 6);
                    ctx.fill();
                    ctx.shadowBlur = 0;

                    // Caution diagonal warning stripes
                    ctx.strokeStyle = "rgba(6, 182, 212, 0.4)";
                    ctx.lineWidth = 2.5;
                    ctx.beginPath();
                    const stripeSpacing = 10 * scaleX;
                    for (let offset = -sBrickHeight; offset < sBrickWidth; offset += stripeSpacing) {
                        ctx.moveTo(Math.max(brickX, brickX + offset), Math.max(brickY, brickY + offset));
                        ctx.lineTo(Math.min(brickX + sBrickWidth, brickX + offset + sBrickHeight), Math.min(brickY + sBrickHeight, brickY + offset + sBrickHeight));
                    }
                    ctx.stroke();

                    // Outer cyan steel frame
                    ctx.strokeStyle = "#06b6d4";
                    ctx.lineWidth = 1.5;
                    ctx.strokeRect(brickX, brickY, sBrickWidth, sBrickHeight);
                } else {
                    // Breakable bricks styling
                    let brickColor = brick.color;
                    let glowColor = brick.color;

                    if (brick.maxHits === 3) {
                        brickColor = brick.status === 3 ? "#fbbf24" : brick.status === 2 ? "#d97706" : "#b45309";
                        glowColor = "#fbbf24";
                    } else if (brick.maxHits === 2) {
                        brickColor = brick.status === 2 ? "#c084fc" : "#818cf8";
                        glowColor = "#c084fc";
                    }

                    ctx.shadowBlur = 10;
                    ctx.shadowColor = glowColor;
                    ctx.fillStyle = brickColor;
                    ctx.beginPath();
                    ctx.roundRect(brickX, brickY, sBrickWidth, sBrickHeight, 4);
                    ctx.fill();
                    ctx.shadowBlur = 0;

                    // Neon borders
                    ctx.strokeStyle = "rgba(255, 255, 255, 0.35)";
                    ctx.lineWidth = 1;
                    ctx.stroke();

                    // Gold/Purple specialized frame borders
                    if (brick.maxHits === 3) {
                        ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
                        ctx.lineWidth = 1.5;
                        ctx.strokeRect(brickX + 3, brickY + 3, sBrickWidth - 6, sBrickHeight - 6);
                    } else if (brick.maxHits === 2) {
                        ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
                        ctx.strokeRect(brickX + 2, brickY + 2, sBrickWidth - 4, sBrickHeight - 4);
                    }

                    // Gloss highlights
                    const highlightGrad = ctx.createLinearGradient(brickX, brickY, brickX, brickY + sBrickHeight / 2);
                    highlightGrad.addColorStop(0, "rgba(255, 255, 255, 0.35)");
                    highlightGrad.addColorStop(1, "rgba(255, 255, 255, 0)");
                    ctx.fillStyle = highlightGrad;
                    ctx.fillRect(brickX + 1, brickY + 1, sBrickWidth - 2, sBrickHeight / 2 - 1);

                    // Dynamic cracks when damaged
                    if (brick.status < brick.maxHits) {
                        ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
                        ctx.lineWidth = 1.2;
                        ctx.beginPath();
                        const seedX = (c * 17) % 7 + 2;
                        const seedY = (r * 11) % 5 + 2;
                        const midX = brickX + sBrickWidth / 2 + (seedX - 5);
                        const midY = brickY + sBrickHeight / 2 + (seedY - 4);

                        ctx.moveTo(midX, midY);
                        ctx.lineTo(brickX + 5, brickY + 5);

                        ctx.moveTo(midX, midY);
                        ctx.lineTo(brickX + sBrickWidth - 5, brickY + sBrickHeight - 5);

                        if (brick.status === 1 && brick.maxHits === 3) {
                            ctx.moveTo(midX, midY);
                            ctx.lineTo(brickX + sBrickWidth / 3, brickY + sBrickHeight - 4);
                            ctx.moveTo(midX, midY);
                            ctx.lineTo(brickX + sBrickWidth - 4, brickY + 4);
                        }
                        ctx.stroke();
                    }
                }
                ctx.restore();
            });
        });

        // --- LEVEL TRANSITION LOGIC ---
        if (breakableBricksLeft === 0 && gameStarted) {
            if (round < maxRounds) {
                const nextRound = round + 1;
                setRound(nextRound);
                const nextConfig = LEVEL_CONFIGS[nextRound - 1];
                
                // Position and set speeds for new round
                ball.current = {
                    x: worldWidth / 2,
                    y: worldHeight - 100,
                    dx: nextConfig.speed,
                    dy: -nextConfig.speed
                };
                paddleX.current = worldWidth / 2 - (worldWidth * PADDLE_WIDTH_RATIO) / 2;
                particles.current = [];
                ballTrail.current = [];
                initBricks(nextRound);
                screenShake.current = 15; // Mega level clear shake!
            } else {
                setGameOver(true);
                setGameStarted(false);
                return;
            }
        }

        // --- MOVE PADDLE ---
        const paddleSpeed = 12 + (round * 0.5);
        if (activeKeys.current.right && paddleX.current < worldWidth - (worldWidth * PADDLE_WIDTH_RATIO)) {
            paddleX.current += paddleSpeed;
        } else if (activeKeys.current.left && paddleX.current > 0) {
            paddleX.current -= paddleSpeed;
        }

        // --- DRAW PADDLE ---
        const sPaddleX = paddleX.current * scaleX;
        const sPaddleY = canvas.height - sPaddleHeight - (20 * scaleY);

        ctx.shadowBlur = 15;
        ctx.shadowColor = "#38bdf8";
        ctx.beginPath();
        ctx.roundRect(sPaddleX, sPaddleY, sPaddleWidth, sPaddleHeight, 8);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.closePath();
        ctx.shadowBlur = 0;

        // --- BALL TRAIL LOGIC ---
        ballTrail.current.push({ x: sBallX, y: sBallY });
        if (ballTrail.current.length > 8) {
            ballTrail.current.shift();
        }

        ballTrail.current.forEach((pos, index) => {
            const alpha = (index + 1) / ballTrail.current.length * 0.35;
            const size = sBallRadius * (0.4 + 0.6 * (index / ballTrail.current.length));
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.shadowBlur = 10;
            ctx.shadowColor = levelConfig.theme.glow;
            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, size, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        });

        // --- MOVE BALL & PHYSICAL BOUNDARIES ---
        const ballNextX = ball.current.x + ball.current.dx;
        const ballNextY = ball.current.y + ball.current.dy;

        // Left/Right walls
        if (ballNextX > worldWidth - (worldWidth * BALL_RADIUS_RATIO) || ballNextX < (worldWidth * BALL_RADIUS_RATIO)) {
            ball.current.dx = -ball.current.dx;
            spawnParticles(sBallX, sBallY, "#ffffff", 4);
            screenShake.current = Math.max(screenShake.current, 2);
        }
        // Top Wall
        if (ballNextY < (worldWidth * BALL_RADIUS_RATIO)) {
            ball.current.dy = -ball.current.dy;
            spawnParticles(sBallX, sBallY, "#ffffff", 4);
            screenShake.current = Math.max(screenShake.current, 2);
        } else if (ballNextY > worldHeight - (worldHeight * PADDLE_HEIGHT_RATIO) - 30) {
            // Paddle Bounce Check
            const pWidth = worldWidth * PADDLE_WIDTH_RATIO;
            if (ball.current.x > paddleX.current && ball.current.x < paddleX.current + pWidth) {
                let hitPos = (ball.current.x - (paddleX.current + pWidth / 2)) / (pWidth / 2);
                ball.current.dx = hitPos * levelConfig.speed * 1.5;
                ball.current.dy = -Math.abs(ball.current.dy);
                
                // Paddle hit kinetics
                spawnParticles(sBallX, sPaddleY, "#ffffff", 6);
                screenShake.current = Math.max(screenShake.current, 3);
            } else if (ballNextY > worldHeight) {
                setGameOver(true);
                setGameStarted(false);
                return;
            }
        }

        ball.current.x += ball.current.dx;
        ball.current.y += ball.current.dy;

        // --- DRAW BALL ---
        ctx.shadowBlur = 12;
        ctx.shadowColor = "#ffffff";
        ctx.beginPath();
        ctx.arc(sBallX, sBallY, sBallRadius, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.closePath();
        ctx.shadowBlur = 0;

        // --- DRAW/UPDATE PARTICLES ---
        particles.current = particles.current.filter((p) => {
            p.x += p.dx;
            p.y += p.dy + p.gravity;
            p.life -= p.decay;

            if (p.life <= 0) return false;

            ctx.save();
            ctx.globalAlpha = p.life;
            ctx.shadowBlur = 5;
            ctx.shadowColor = p.color;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            return true;
        });

        ctx.restore(); // Restore from Screen Shake

        animationFrameId.current = requestAnimationFrame(draw);
    }, [round, initBricks, gameStarted, worldWidth, worldHeight, isPaused, maxRounds, PADDLE_WIDTH_RATIO, PADDLE_HEIGHT_RATIO, BALL_RADIUS_RATIO, BRICK_PADDING_RATIO, BRICK_OFFSET_TOP_RATIO, BRICK_COLS, spawnParticles]);

    useEffect(() => {
        if (gameStarted && !gameOver) {
            animationFrameId.current = requestAnimationFrame(draw);
        }
        return () => cancelAnimationFrame(animationFrameId.current);
    }, [gameStarted, gameOver, draw]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowRight") activeKeys.current.right = true;
            if (e.key === "ArrowLeft") activeKeys.current.left = true;
            if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", " "].includes(e.key)) e.preventDefault();
        };
        const handleKeyUp = (e) => {
            if (e.key === "ArrowRight") activeKeys.current.right = false;
            if (e.key === "ArrowLeft") activeKeys.current.left = false;
        };

        const handleTouch = (e) => {
            const canvas = canvasRef.current;
            if (!canvas || !gameStarted || gameOver) return;
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const touch = e.touches[0];
            const touchX = touch.clientX - rect.left;

            // Map to virtual coordinates
            const pWidth = worldWidth * PADDLE_WIDTH_RATIO;
            let targetX = (touchX / rect.width) * worldWidth - (pWidth / 2);

            if (targetX < 0) targetX = 0;
            if (targetX > worldWidth - pWidth) targetX = worldWidth - pWidth;

            paddleX.current = targetX;
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);

        const canvas = canvasRef.current;
        if (canvas) {
            canvas.addEventListener("touchstart", handleTouch, { passive: false });
            canvas.addEventListener("touchmove", handleTouch, { passive: false });
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            if (canvas) {
                canvas.removeEventListener("touchstart", handleTouch);
                canvas.removeEventListener("touchmove", handleTouch);
            }
        };
    }, [gameStarted, gameOver, worldWidth, PADDLE_WIDTH_RATIO]);

    const startGame = () => {
        setGameOver(false);
        setScore(0);
        setRound(1);
        setGameStarted(true);
        paddleX.current = worldWidth / 2 - (worldWidth * PADDLE_WIDTH_RATIO) / 2;
        const firstConfig = LEVEL_CONFIGS[0];
        ball.current = { 
            x: worldWidth / 2, 
            y: worldHeight - 100, 
            dx: firstConfig.speed, 
            dy: -firstConfig.speed 
        };
        initBricks(1);
        particles.current = [];
        ballTrail.current = [];
        screenShake.current = 0;
    };

    const currentLevelConfig = LEVEL_CONFIGS[round - 1] || LEVEL_CONFIGS[0];

    return (
        <div className="brK_container">
            {/* Absolute floating exit button on mobile */}
            <button onClick={onBack} className="brK_floating-exit-btn">EXIT</button>

            <div className="brK_game-area">
                {/* 1. Mobile HUD Header (Title, Round, Score) */}
                <div className="brK_mobile-hud-header">
                    <div className="brK_mobile-hud-badge">PARANOID</div>
                    <div className="brK_mobile-hud-stats">
                        <div className="brK_mobile-stat-pill">
                            <span className="label">ROUND</span>
                            <span className="value">{round} / {maxRounds}</span>
                        </div>
                        <div className="brK_mobile-hud-divider"></div>
                        <div className="brK_mobile-stat-pill">
                            <span className="label">SCORE</span>
                            <span className="value">{score}</span>
                        </div>
                    </div>
                </div>

                {/* 2. Sidebar Details (Level Info, Tactical Intel, Exit button) */}
                {/* On mobile, this will render at the top below the header stats */}
                <div className="brK_sidebar">
                    {/* Desktop HUD (hidden on mobile) */}
                    <div className="brK_desktop-hud-wrapper">
                        <div className="brK_hud-top">
                            <div className="brK_title-badge">PARANOID</div>
                        </div>

                        <div className="brK_stat-grid">
                            <div className="brK_stat-card">
                                <label>ROUND</label>
                                <span>{round} / {maxRounds}</span>
                            </div>
                            <div className="brK_stat-card">
                                <label>SCORE</label>
                                <span>{score}</span>
                            </div>
                        </div>
                    </div>

                    <div className="brK_level-info-card">
                        <label>LEVEL MISSION</label>
                        <h2 className="brK_level-name">{currentLevelConfig.name}</h2>
                        <span 
                            className="brK_difficulty-badge" 
                            style={{ 
                                color: currentLevelConfig.diffColor, 
                                borderColor: `${currentLevelConfig.diffColor}50`, 
                                backgroundColor: `${currentLevelConfig.diffColor}15` 
                            }}
                        >
                            {currentLevelConfig.difficulty}
                        </span>
                        <p className="brK_level-desc">{currentLevelConfig.description}</p>
                    </div>

                    

                    <button onClick={onBack} className="brK_exit-btn">EXIT</button>
                </div>

                {/* 3. Canvas/Playing Frame */}
                {/* On mobile, this will render in the center/bottom */}
                <div className="brK_canvas-frame" ref={wrapperRef}>
                    <canvas id="paranoid-canvas" ref={canvasRef} />
                    {!gameStarted && (
                        <div className="brK_overlay">
                            <h1>PARANOID</h1>
                            <p>Destroy all energy bricks to complete the mission</p>
                            <button className="brK_btn" onClick={startGame}>Start Mission</button>
                        </div>
                    )}
                    {gameOver && (
                        <div className="brK_overlay">
                            <h1>{round >= maxRounds ? "VICTORY" : "GAME OVER"}</h1>
                            <div className="brK_final-scores">
                                <div><label>SCORE</label> <span>{score}</span></div>
                                <div><label>ROUND</label> <span>{round}</span></div>
                            </div>
                            <button className="brK_btn" onClick={startGame}>Try Again</button>
                        </div>
                    )}
                </div>

                {/* 4. Touch Arrow Controls at the absolute bottom */}
                <div className="brK_mobile-controls">
                    <button
                        onMouseDown={() => activeKeys.current.left = true}
                        onMouseUp={() => activeKeys.current.left = false}
                        onTouchStart={(e) => { e.preventDefault(); activeKeys.current.left = true; }}
                        onTouchEnd={(e) => { e.preventDefault(); activeKeys.current.left = false; }}
                        className="brK_ctrl-btn"
                    >←</button>
                    <button
                        onMouseDown={() => activeKeys.current.right = true}
                        onMouseUp={() => activeKeys.current.right = false}
                        onTouchStart={(e) => { e.preventDefault(); activeKeys.current.right = true; }}
                        onTouchEnd={(e) => { e.preventDefault(); activeKeys.current.right = false; }}
                        className="brK_ctrl-btn"
                    >→</button>
                </div>
            </div>
        </div>
    );
};

export default BreakoutGame;